import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trophy, Award, CheckCircle } from 'lucide-react';

export function QuizFailModal({ isOpen, onRetry, onClose, score, passPercentage }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-3xl text-center border-0 bg-white shadow-2xl">
        <div className="py-8 relative z-10">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">💪</span>
          </div>
          
          <h2 className="text-3xl font-bold text-navy mb-4">We know you can do it!</h2>
          <div className="bg-primary-blue/10 rounded-2xl py-3 px-6 mb-4">
            <p className="text-sm text-[#4D4D4D] mb-1">Your Score</p>
            <p className="text-3xl font-bold text-primary-blue">{score}%</p>
          </div>
          <p className="text-lg text-[#4D4D4D] mb-8 font-light leading-relaxed">
            You need <span className="font-bold text-navy">{passPercentage}%</span> or higher to pass.<br />
            Try again — we believe in you!
          </p>
          
          <Button
            onClick={onRetry}
            className="w-full bg-primary-blue hover:bg-[#1874CD] text-white h-12 text-lg rounded-2xl transition-smooth btn-press font-bold"
          >
            Let's do it!! 💪
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function QuizCelebrationModal({ isOpen, onClose, score }) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Confetti Layer - Outside Dialog to ensure it appears above everything */}
      {isOpen && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
          {/* Left corner confetti */}
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={150}
            gravity={0.3}
            initialVelocityX={8}
            initialVelocityY={-25}
            confettiSource={{
              x: 0,
              y: windowSize.height,
              w: 10,
              h: 10
            }}
            tweenDuration={1200}
            colors={['#1E90FF', '#00CED1', '#FFD700', '#FF69B4', '#32CD32', '#FF6347']}
          />
          {/* Right corner confetti */}
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={150}
            gravity={0.3}
            initialVelocityX={-8}
            initialVelocityY={-25}
            confettiSource={{
              x: windowSize.width,
              y: windowSize.height,
              w: 10,
              h: 10
            }}
            tweenDuration={1200}
            colors={['#1E90FF', '#00CED1', '#FFD700', '#FF69B4', '#32CD32', '#FF6347']}
          />
        </div>
      )}
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md rounded-3xl text-center border-0 bg-white shadow-2xl" style={{ zIndex: 9998 }}>
          <div className="py-8 relative z-10">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-12 h-12 text-green-600" strokeWidth={2} />
          </div>
          
          <h2 className="text-4xl font-bold text-navy mb-3">🎉 Congratulations!</h2>
          <p className="text-lg text-[#4D4D4D] mb-6 font-light">You passed the quiz — keep going!</p>
          <div className="bg-primary-blue/10 rounded-2xl py-3 px-6 mb-8">
            <p className="text-sm text-[#4D4D4D] mb-1">Your Score</p>
            <p className="text-3xl font-bold text-primary-blue">{score}%</p>
          </div>
          
          <Button
            onClick={onClose}
            className="w-full bg-primary-blue hover:bg-[#1874CD] text-white h-12 text-lg rounded-2xl transition-smooth btn-press"
          >
            Continue
          </Button>
        </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ModuleCelebrationModal({ isOpen, onClose, moduleName, onContinue }) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Confetti Layer - Outside Dialog to ensure it appears above everything */}
      {isOpen && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
          {/* Left corner confetti */}
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={250}
            gravity={0.3}
            initialVelocityX={10}
            initialVelocityY={-30}
            confettiSource={{
              x: 0,
              y: windowSize.height,
              w: 10,
              h: 10
            }}
            tweenDuration={1500}
            colors={['#FFD700', '#FFA500', '#FF6347', '#1E90FF', '#32CD32', '#FF69B4', '#00CED1']}
          />
          {/* Right corner confetti */}
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={250}
            gravity={0.3}
            initialVelocityX={-10}
            initialVelocityY={-30}
            confettiSource={{
              x: windowSize.width,
              y: windowSize.height,
              w: 10,
              h: 10
            }}
            tweenDuration={1500}
            colors={['#FFD700', '#FFA500', '#FF6347', '#1E90FF', '#32CD32', '#FF69B4', '#00CED1']}
          />
        </div>
      )}
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg rounded-3xl text-center border-0 bg-white shadow-2xl" style={{ zIndex: 9998 }}>
          <div className="py-12 relative z-10">
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto animate-bounce shadow-lg">
              <Trophy className="w-14 h-14 text-white" strokeWidth={2} />
            </div>
            <div className="absolute -top-2 -right-2 left-0 right-0 mx-auto w-fit">
              <Award className="w-8 h-8 text-yellow-500 animate-spin" style={{ animationDuration: '3s' }} strokeWidth={2} />
            </div>
          </div>
          
          <h2 className="text-4xl font-bold text-navy mb-3">🏆 Congratulations!</h2>
          <p className="text-xl text-[#4D4D4D] mb-6">
            You have successfully completed<br />
            <span className="font-bold text-primary-blue">{moduleName}</span>
          </p>
          <p className="text-base text-[#4D4D4D] mb-8 font-light">Keep learning and growing! You're making excellent progress!</p>
          
          <Button
            onClick={() => {
              onContinue();
              onClose();
            }}
            className="w-full bg-primary-blue hover:bg-[#1874CD] text-white h-12 text-lg rounded-2xl transition-smooth btn-press"
          >
            Continue to Next Module →
          </Button>
        </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
